import React, { useEffect } from 'react';
import { useWatch } from 'react-hook-form';
import { FormGroup, FormRow } from '@cad-arc';
import { useIntegratedForm } from '../../../../forms/use-integrated-form';
import { EntityPanelFormVehicleBaseKey, EntityPanelVehiclesForm } from '../types';
import { FormSelect } from '../../../../forms/integrated-form-controls/form-select';
import { FormInput } from '../../../../forms/integrated-form-controls/form-input';
import { EntityPanelFormVehicleCardControls } from './entity-panel-form-vehicle-card-controls';
import { EntityPanelFormBaseVehicleInputs } from './entity-panel-form-vehicle-base-inputs';
import { UseTrackedFieldArrayRemove } from '../../../../forms/use-tracked-field-array';
import { EventPanelOptions } from '@mark43/cad-api';

interface EntityPanelVehicleFieldsetProps {
    index: number;
    remove: UseTrackedFieldArrayRemove;
    entityPanelOptions: EventPanelOptions;
    allowMembersControl?: boolean;
    displayAsCards?: boolean;
    baseKey: EntityPanelFormVehicleBaseKey;
    isNewEntity: boolean;
    isReadOnly?: boolean;
    entityNumber?: string;
    eventId?: number;
    userManuallyAddedField?: boolean;
}

function _EntityPanelFormVehiclesFieldset(
    props: EntityPanelVehicleFieldsetProps
): React.ReactElement {
    const { isNewEntity, index, entityPanelOptions, remove, baseKey, isReadOnly } = props;
    const { form } = useIntegratedForm<EntityPanelVehiclesForm>();

    const isPersisted = useWatch({
        control: form.control,
        name: `${baseKey}.${index}.isPersisted`,
    });

    const vehicleId = useWatch({
        control: form.control,
        name: `${baseKey}.${index}.id`,
    });

    const showInvolvementTypeOther = useWatch({
        control: form.control,
        name: `${props.baseKey}.${index}.involvementTypeOption`,
    })?.isOther;

    useEffect(
        function resetInvolvementTypeOther() {
            if (!showInvolvementTypeOther) {
                form.setValue(`${baseKey}.${index}.involvementTypeOther`, null);
            }
        },
        [form, index, baseKey, showInvolvementTypeOther]
    );

    return (
        <>
            <EntityPanelFormVehicleCardControls
                remove={remove}
                isNewEntity={isNewEntity}
                allowMembersControl={props.allowMembersControl}
                baseKey={baseKey}
                index={index}
                isReadOnly={isReadOnly}
                entityNumber={props.entityNumber}
                eventId={props.eventId}
            />
            <FormGroup
                style={{ flex: 1, paddingBottom: 'var(--arc-space-1)' }}
                data-testid={`vehicle-card-${vehicleId}`}
            >
                <EntityPanelFormBaseVehicleInputs
                    index={index}
                    baseKey={baseKey}
                    entityPanelOptions={entityPanelOptions}
                    isReadOnly={isReadOnly}
                    userManuallyAddedField={props.userManuallyAddedField}
                />
                <FormRow>
                    <FormSelect
                        length={showInvolvementTypeOther ? 'md' : undefined}
                        control={form.control}
                        label="Involvement Type"
                        options={entityPanelOptions.vehicleInvolvementTypeOptions}
                        name={`${props.baseKey}.${index}.involvementTypeOption`}
                        asEditable={!!isPersisted}
                        eventNotificationName="VEHICLE_INVOLVEMENT_TYPE"
                        mode={isReadOnly ? 'read-only' : undefined}
                    />
                    {showInvolvementTypeOther && (
                        <FormInput
                            label="Description"
                            control={form.control}
                            name={`${props.baseKey}.${index}.involvementTypeOther`}
                            asEditable={!!isPersisted}
                            length="md"
                            mode={isReadOnly ? 'read-only' : undefined}
                        />
                    )}
                </FormRow>
            </FormGroup>
        </>
    );
}

export const EntityPanelFormVehiclesFieldset = React.memo(
    _EntityPanelFormVehiclesFieldset,
    (prevProps, nextProps) => {
        return (
            prevProps.entityPanelOptions === nextProps.entityPanelOptions &&
            prevProps.remove === nextProps.remove &&
            prevProps.index === nextProps.index &&
            prevProps.isNewEntity === prevProps.isNewEntity
        );
    }
);
